import { useFocusEffect, useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import {
  Body2,
  Button,
  Caption,
  generateShadow,
  Touchable,
  useAlert,
  useTheme,
  VectorIcon,
} from "capsule"
import React, { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Platform, Pressable, ScrollView, StyleSheet, View } from "react-native"

import { PractitionerHomeNS } from "../../features/i18n/constants"
import { tabSizeLand } from "../../ThemeApp"
import { ICodesParamList } from "../MainScreen/MainTabs"

type NavigationProp = StackNavigationProp<ICodesParamList>

interface EligibilityQuestion {
  id: string
  hasInfo?: boolean
}

const questions: EligibilityQuestion[] = [
  {
    id: "primaryReconstruction",
  },
  {
    id: "noMultiLigament",
  },
  {
    id: "over18",
  },
  {
    id: "socialSecurity",
  },
  {
    id: "smartphone",
  },
  {
    id: "autonomous",
    hasInfo: true,
  },
]

const PrescriptionLCAEligibilityScreen: FC = () => {
  const {
    colors: {
      primary,
      surface: { background, disabled: disabledGray },
      white: { highEmphasis: white },
      black: { mediumEmphasis: mediumGray, separator: separatorColor },
    },
    dimensions: { spacing },
    screenStyle,
  } = useTheme()
  const { t } = useTranslation()
  const navigation = useNavigation<NavigationProp>()
  const { showDialog } = useAlert()

  const [answers, setAnswers] = useState<Record<string, boolean | null>>(
    questions.reduce((acc, q) => ({ ...acc, [q.id]: null }), {}),
  )

  useFocusEffect(
    useCallback(() => {
      setAnswers(questions.reduce((acc, q) => ({ ...acc, [q.id]: null }), {}))
    }, []),
  )

  const handleAnswer = useCallback((questionId: string, value: boolean) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }))
  }, [])

  const allAnswered = useMemo(() => questions.every(q => answers[q.id] !== null), [answers])

  const allEligible = useMemo(() => questions.every(q => answers[q.id] === true), [answers])

  const onValidate = useCallback(() => {
    if (allEligible) {
      navigation.navigate("PrescriptionLCA")
    } else {
      navigation.navigate("PrescriptionLCAIneligible")
    }
  }, [allEligible, navigation])

  const showInfoModal = useCallback(() => {
    showDialog({
      type: "button",
      title: t(`${PractitionerHomeNS}:prescriptionLCAEligibility.infoModal.title`),
      titleStyle: { textAlign: "center" },
      contentContainerStyle:
        Platform.OS === "web"
          ? { maxWidth: "30%", transform: [{ translateX: tabSizeLand / 2 }] }
          : undefined,
      positive: {
        label: t("button.ok"),
      },
      children: (
        <View style={{ paddingHorizontal: spacing * 2 }}>
          <View style={{ height: 1, backgroundColor: separatorColor }} />
          <Body2 style={{ marginVertical: spacing, lineHeight: 20 }}>
            {t(`${PractitionerHomeNS}:prescriptionLCAEligibility.infoModal.content`)}
          </Body2>
        </View>
      ),
    })
  }, [showDialog, t, spacing, separatorColor])

  const s = useMemo(
    () => ({
      container: [
        styles.container,
        {
          backgroundColor: background,
        },
      ],
      scrollContent: {
        padding: spacing,
        paddingBottom: spacing,
      },
      header: {
        marginBottom: spacing * 0.5,
      },
      description: {
        color: mediumGray,
        marginBottom: spacing * 0.25,
      },
      questionsCard: [
        styles.questionsCard,
        {
          backgroundColor: white,
          padding: spacing * 0.75,
          borderRadius: 8,
          ...generateShadow(1),
        },
      ],
      questionRow: [
        styles.questionRow,
        {
          paddingVertical: spacing * 0.65,
          borderBottomWidth: 1,
          borderBottomColor: separatorColor,
        },
      ],
      lastQuestionRow: {
        borderBottomWidth: 0,
      },
      questionTextContainer: {
        flex: 1,
        paddingRight: spacing,
      },
      questionText: {
        fontSize: 13,
        lineHeight: 18,
      },
      answersContainer: [
        styles.answersContainer,
        {
          marginLeft: spacing,
        },
      ],
      radioButton: [
        styles.radioButton,
        {
          width: 56,
          height: 44,
          borderRadius: 6,
          borderWidth: 1,
          borderColor: disabledGray,
        },
      ],
      radioButtonSelected: {
        borderColor: primary,
        backgroundColor: primary,
      },
      radioButtonYes: {
        marginRight: spacing / 2,
      },
      radioLabel: {
        fontSize: 14,
        fontWeight: "500" as const,
      },
      radioLabelSelected: {
        color: white,
      },
      validateButton: {
        marginTop: spacing,
      },
      infoIcon: {
        marginLeft: spacing / 2,
        padding: spacing / 4,
      },
    }),
    [background, spacing, white, mediumGray, primary, disabledGray, separatorColor],
  )

  const renderQuestion = useCallback(
    (question: EligibilityQuestion, index: number) => {
      const answer = answers[question.id]
      const isLast = index === questions.length - 1

      return (
        <View key={question.id} style={[s.questionRow, isLast && s.lastQuestionRow]}>
          <View style={s.questionTextContainer}>
            <View style={styles.questionTextRow}>
              <Body2 style={s.questionText}>
                {t(`${PractitionerHomeNS}:prescriptionLCAEligibility.questions.${question.id}`)}
              </Body2>
              {question.hasInfo ? (
                <Touchable onPress={showInfoModal} style={s.infoIcon}>
                  <VectorIcon name="info" category="MaterialIcons" size={16} color={primary} />
                </Touchable>
              ) : null}
            </View>
          </View>
          <View style={s.answersContainer}>
            <Pressable
              onPress={() => handleAnswer(question.id, true)}
              style={[s.radioButton, s.radioButtonYes, answer === true && s.radioButtonSelected]}
              android_ripple={{ color: primary, borderless: false }}
            >
              <Caption style={[s.radioLabel, answer === true && s.radioLabelSelected]}>
                {t("button.yes").toUpperCase()}
              </Caption>
            </Pressable>
            <Pressable
              onPress={() => handleAnswer(question.id, false)}
              style={[s.radioButton, answer === false && s.radioButtonSelected]}
              android_ripple={{ color: primary, borderless: false }}
            >
              <Caption style={[s.radioLabel, answer === false && s.radioLabelSelected]}>
                {t("button.no").toUpperCase()}
              </Caption>
            </Pressable>
          </View>
        </View>
      )
    },
    [answers, s, handleAnswer, primary, showInfoModal, t],
  )

  return (
    <View style={[s.container, screenStyle]}>
      <ScrollView contentContainerStyle={s.scrollContent}>
        <View style={s.header}>
          <Body2 style={s.description}>
            {t(`${PractitionerHomeNS}:prescriptionLCAEligibility.description`)}
          </Body2>
        </View>

        <View style={s.questionsCard}>{questions.map(renderQuestion)}</View>

        <Button
          mode="contained"
          disabled={!allAnswered}
          onPress={onValidate}
          style={s.validateButton}
        >
          {t(`${PractitionerHomeNS}:prescriptionLCAEligibility.button.validate`)}
        </Button>
      </ScrollView>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  questionsCard: {
    flexDirection: "column",
  },
  questionRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  questionTextRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  answersContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  radioButton: {
    justifyContent: "center",
    alignItems: "center",
  },
})

export default PrescriptionLCAEligibilityScreen
